import React, { useRef, useState } from 'react';
import Tool, {
  type IToolAvailableInput,
  type ToolButtonComponent,
} from '../Tool';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../../toolbars/ToolButton';
import WhiteboardController from '../../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import ToolWireframe from './ToolWireframe';
import styles from '../../styles.module.css';
import ToolButtonWithLabel from '../../toolbars/ToolButtonWithLabel';
import { isWireframe } from '../../objects/guards';
import type Wireframe from '../../objects/Wireframe';
import { camelToSentenceCase } from '../../utils/string';
import { WIREFRAME_SELECTION_ERROR_MESSAGE } from '../../utils/tools';

interface PopupProps {
  currentPosition: string;
  setCurrentPosition: (position: string) => void;
  selection: Wireframe | fabric.Object;
  hidePopup: () => void;
}

const Popup: React.FC<PopupProps> = ApplicationView(
  ({ currentPosition, setCurrentPosition, selection, hidePopup }) => {
    if (!isWireframe(selection)) {
      console.error(WIREFRAME_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const controller = useController(WhiteboardController);
    const positions = selection.wireframe.labelPositions();

    const setPosition = position => {
      setCurrentPosition(position);
      selection.wireframe.setLabelPosition(position);
      hidePopup();
      Tool.toolActionComplete({ controller });
    };

    return (
      <div>
        {positions.map(position => (
          <ToolButton
            active={position === currentPosition}
            className={styles.stringToolButton}
            key={position}
            onClick={() => setPosition(position)}
          >
            {camelToSentenceCase(position)}
          </ToolButton>
        ))}
      </div>
    );
  }
);

const LabelToolButton: ToolButtonComponent = ApplicationView(
  ({ selection }) => {
    const tippyRef = useRef<TippyInstance>();
    if (!isWireframe(selection)) {
      console.error(WIREFRAME_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const initialPosition = selection.wireframe.labelPosition();
    const [position, setPosition] = useState(initialPosition);

    return (
      <ToolButtonWithLabel
        popup={
          <Popup
            currentPosition={position}
            setCurrentPosition={setPosition}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        label={'Label'}
        tippyRef={tippyRef}
      >
        {camelToSentenceCase(position)}
      </ToolButtonWithLabel>
    );
  }
);

class ToolWireframeLabelPosition extends ToolWireframe {
  static toolName = 'Label position';

  static available({ selection }: IToolAvailableInput) {
    return selection?.type === 'wireframe';
  }

  static buttonComponent() {
    return LabelToolButton;
  }
}

export default ToolWireframeLabelPosition;
